import { FileSignatureOutlined } from '@vindr/icons';
import { useCreateEsign } from '@vindr/services/actions/esign.action';
import { CreateEsignParams } from '@vindr/services/apis/esign.api';
import { stringToBase64 } from '@vindr/utils/converter';
import useToggle from '@vindr/utils/hooks/useToggle';
import { Button, Form, Input, Modal, Typography } from 'antd';
import { MessageInstance } from 'antd/es/message/interface';
import { ReactElement, cloneElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PrintModal from './Print';
import { MESSAGE } from '@vindr/services/constants/message.constant';

const Title = Typography.Title;

const SignDigital = ({
  message,
  report_id,
  content,
  preview,
  report_version_id,
  onSuccess,
  buttonText,
  ...props
}: Parameters<typeof Button>['0'] & {
  message: MessageInstance;
  report_id: CreateEsignParams['report_id'];
  content: CreateEsignParams['content'];
  report_version_id: CreateEsignParams['report_version_id'];
  preview: ReactElement<Parameters<typeof PrintModal>['0']>;
  onSuccess?: () => void;
  buttonText?: string;
}) => {
  const { t } = useTranslation('Vindoc');
  const { value: open, setFalse: onClose, setTrue: onOpen } = useToggle();
  const { value: openPreview, setFalse: onClosePreview, setTrue: onOpenPreview } = useToggle();
  const [form] = Form.useForm();
  const { mutate: sendSign } = useCreateEsign();
  const { value: isLoading, setTrue: setLoading, setFalse: setLoaded } = useToggle();

  const onSubmit = () => {
    setLoading();
    form
      .validateFields()
      .then(async (value) => {
        sendSign(
          {
            content: stringToBase64(content),
            report_id,
            credential: value?.password,
            report_version_id,
          },
          {
            onSuccess: () => {
              //? Wait for esign status update before close
              setTimeout(() => {
                message.success({ content: t(MESSAGE.SUCCESS.SCD_33001) });
                form.resetFields();
                setLoaded();
                if (typeof onSuccess === 'function') onSuccess?.();
                onClose();
                onClosePreview();
              }, 3000);
            },
            onError: (e) =>
              message.error({
                content: t(MESSAGE.ERROR[e?.response?.data?.error_code] || MESSAGE.ERROR.EMSG_0001),
              }),
          }
        );
      })
      .catch((err) => {
        console.log(err);
      });
  };

  return (
    <>
      <Button
        className="ml-2 mt-1 flex min-w-16 items-center justify-center"
        size="small"
        onClick={onOpenPreview}
        {...props}
      >
        <FileSignatureOutlined />
        {t(buttonText || 'E-sign')}
      </Button>
      {preview &&
        cloneElement(preview, {
          handleClose: () => (onClose(), onClosePreview()),
          onOk: () => onOpen(),
          open: true,
          zIndex: openPreview ? 1010 : -1,
        })}
      {open && (
        <Modal
          cancelText={t('Cancel')}
          okButtonProps={{ loading: isLoading }}
          okText={t('Sign Report')}
          open={open}
          width={400}
          zIndex={9999}
          title={
            <Title className="mb-6" level={3}>
              {t('Enter Your Password')}
            </Title>
          }
          onCancel={() => (form.setFieldValue('password', undefined), onClose())}
          onOk={onSubmit}
        >
          <Form className="w-full" form={form} layout="inline">
            <Form.Item
              className="mb-3 w-full"
              name="password"
              rules={[{ required: true, message: t('Please input password') }]}
            >
              <Input.Password className="w-full" placeholder={t('Password')} width={'100%'} />
            </Form.Item>
          </Form>
        </Modal>
      )}
    </>
  );
};

export default SignDigital;
